/**
 * QuickLogSheet — shared bottom-sheet modal for logging lead communications.
 * Extracted from LeadsListPage for reuse in My Queue and other pages.
 */

import { useState } from 'react';
import { AlertCircle, CalendarClock, Mail, MessageSquare, Phone, Plus, Voicemail, X } from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { LeadCommunicationType, LEAD_COMMUNICATION_TYPE_LABEL } from '../../types/admin';

// ── Helpers ──────────────────────────────────────────────────────────────────

function getDefaultFollowUp(): string {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return d.toISOString().slice(0, 10);
}

function getTodayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDateDisplay(value: string): string | null {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

// ── Presets ──────────────────────────────────────────────────────────────────

interface QuickLogPreset {
  label: string;
  type: LeadCommunicationType;
  note: string;
  openNote: boolean;
  icon: typeof Phone;
}

const QUICK_LOG_PRESETS: QuickLogPreset[] = [
  { label: 'Called — no answer', type: 'phone_call', note: 'No answer', openNote: false, icon: Phone },
  { label: 'Called — left voicemail', type: 'voicemail', note: 'Left voicemail', openNote: false, icon: Voicemail },
  { label: 'Called — spoke with customer', type: 'phone_call', note: '', openNote: true, icon: Phone },
  { label: 'Sent email', type: 'email', note: '', openNote: true, icon: Mail },
];

const CUSTOM_TYPES: { type: LeadCommunicationType; icon: typeof Phone }[] = [
  { type: 'phone_call', icon: Phone },
  { type: 'email', icon: Mail },
  { type: 'text_message', icon: MessageSquare },
  { type: 'voicemail', icon: Voicemail },
];

// ── Component ────────────────────────────────────────────────────────────────

export function QuickLogSheet({
  leadName,
  onSubmit,
  onClose,
}: {
  leadName: string;
  onSubmit: (type: LeadCommunicationType, note: string, nextFollowUp: string) => Promise<void>;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'presets' | 'custom'>('presets');
  const [customType, setCustomType] = useState<LeadCommunicationType>('phone_call');
  const [customNote, setCustomNote] = useState('');
  const [presetNote, setPresetNote] = useState('');
  const [activePreset, setActivePreset] = useState<QuickLogPreset | null>(null);
  const [followUp, setFollowUp] = useState(getDefaultFollowUp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handlePresetTap(preset: QuickLogPreset) {
    if (preset.openNote) {
      setActivePreset(preset);
      setPresetNote('');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSubmit(preset.type, preset.note, followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  async function handlePresetSubmit() {
    if (!activePreset) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(activePreset.type, presetNote.trim() || activePreset.note || activePreset.label, followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  async function handleCustomSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!customNote.trim()) { setError('Note is required'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit(customType, customNote.trim(), followUp);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to log communication');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                      md:rounded-2xl md:max-w-sm md:w-full md:pb-6">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-midnight">Log Communication</h2>
            <p className="text-[12px] text-slate truncate mt-0.5">{leadName}</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={16} /></button>
        </div>

        {error && (
          <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 mb-3">
            <AlertCircle size={14} className="shrink-0 text-rose mt-0.5" />
            <p className="text-[12px] text-rose">{error}</p>
          </div>
        )}

        {/* Next Follow-Up date */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-mist mb-3">
          <CalendarClock size={14} className="text-plum shrink-0" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate">Follow-up</span>
          <div className="relative ml-auto">
            <input type="date" value={followUp} min={getTodayStr()}
              onChange={e => setFollowUp(e.target.value)}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
            <span className="text-[12px] font-semibold text-plum">
              {formatDateDisplay(followUp) ?? 'Set date'}
            </span>
          </div>
        </div>

        {mode === 'presets' && !activePreset && (
          <div className="flex flex-col gap-2">
            {QUICK_LOG_PRESETS.map(preset => {
              const Icon = preset.icon;
              return (
                <button
                  key={preset.label}
                  type="button"
                  disabled={saving}
                  onClick={() => handlePresetTap(preset)}
                  className="w-full flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl border border-stone
                             bg-white text-left text-small font-medium text-midnight
                             hover:border-plum/30 active:bg-mist transition-all duration-150
                             disabled:opacity-50"
                >
                  <Icon size={16} className="shrink-0 text-plum" />
                  {preset.label}
                  {!preset.openNote && (
                    <span className="ml-auto text-[10px] text-slate/60 uppercase font-bold tracking-wider">1-tap</span>
                  )}
                </button>
              );
            })}

            <button
              type="button"
              disabled={saving}
              onClick={() => setMode('custom')}
              className="w-full flex items-center gap-3 min-h-[44px] px-4 py-2.5 rounded-xl border border-stone
                         bg-white text-left text-small font-medium text-midnight
                         hover:border-plum/30 active:bg-mist transition-all duration-150
                         disabled:opacity-50"
            >
              <Plus size={16} className="shrink-0 text-slate" />
              Custom…
            </button>

            {saving && (
              <div className="flex justify-center py-3">
                <LoadingSpinner size="sm" />
              </div>
            )}
          </div>
        )}

        {/* Preset with open note input */}
        {mode === 'presets' && activePreset && (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-plum/5 border border-plum/20">
              {(() => { const Icon = activePreset.icon; return <Icon size={14} className="text-plum shrink-0" />; })()}
              <span className="text-[12px] font-semibold text-plum">{activePreset.label}</span>
              <button type="button" onClick={() => setActivePreset(null)}
                className="ml-auto text-[11px] text-slate hover:text-midnight">Change</button>
            </div>
            <textarea
              value={presetNote}
              onChange={e => setPresetNote(e.target.value)}
              placeholder="Add details (optional)…"
              rows={2}
              autoFocus
              className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                         placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                         focus:border-plum resize-none"
            />
            <button
              type="button"
              disabled={saving}
              onClick={handlePresetSubmit}
              className="w-full h-11 rounded-xl bg-plum text-white text-small font-semibold
                         flex items-center justify-center gap-2
                         hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
            >
              {saving ? <LoadingSpinner size="sm" /> : 'Log Communication'}
            </button>
          </div>
        )}

        {/* Custom mode */}
        {mode === 'custom' && (
          <form onSubmit={handleCustomSubmit} className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-1.5">
              {CUSTOM_TYPES.map(({ type, icon: Icon }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setCustomType(type)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all duration-150
                    ${customType === type ? 'bg-plum text-white' : 'bg-mist text-slate hover:text-midnight'}`}
                >
                  <Icon size={12} />
                  {LEAD_COMMUNICATION_TYPE_LABEL[type]}
                </button>
              ))}
            </div>
            <textarea
              value={customNote}
              onChange={e => setCustomNote(e.target.value)}
              placeholder="What happened?"
              rows={2}
              autoFocus
              className="w-full rounded-xl border border-stone bg-white px-4 py-3 text-small text-midnight
                         placeholder:text-slate/50 focus:outline-none focus:ring-2 focus:ring-plum/30
                         focus:border-plum resize-none"
            />
            <div className="flex gap-2">
              <button type="button" onClick={() => setMode('presets')}
                className="h-11 px-4 rounded-xl border border-stone text-small font-semibold text-slate
                           hover:text-midnight active:bg-mist transition-colors duration-150">
                Back
              </button>
              <button
                type="submit"
                disabled={saving || !customNote.trim()}
                className="flex-1 h-11 rounded-xl bg-plum text-white text-small font-semibold
                           flex items-center justify-center gap-2
                           hover:bg-rose active:opacity-80 disabled:opacity-50 transition-colors duration-150"
              >
                {saving ? <LoadingSpinner size="sm" /> : 'Log Communication'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
